import React, { useMemo } from 'react';
import { AppointmentDetailsDTO } from 'artifacts/dtos';
import IntakeStepList, { IntakeStep } from 'components/common/intakeStepList';
import { entryScreenContent } from 'templates/entryScreen';
import { NullableCommunique } from 'types/types';

type Props = {
  appointmentData: AppointmentDetailsDTO | null;
  communiqueData: NullableCommunique;
};

function IntakeCompletionStatus({
  appointmentData,
  communiqueData,
}: Props) {
  const statusItems = useMemo(() => {
    const appointmentCommunique = appointmentData?.appointment?.confirmAppointmentCommunique;
    const requestedForms = appointmentCommunique?.sendForms;
    const submissionStatus = appointmentCommunique?.intakeSubmissionStatus;
    const isHpiEnabled = requestedForms?.hpi ?? communiqueData?.sendForms?.hpi ?? false;
    const isMedicalHistoryEnabled = requestedForms?.medicalHistory
      ?? communiqueData?.sendForms?.medicalHistory
      ?? false;
    const hasForms = Boolean(
      communiqueData?.sendForms?.consentForm
      || communiqueData?.hasAgreementForms
      || communiqueData?.hasCCMConsentForms
      || communiqueData?.formWorkflowId,
    );

    return [
      isHpiEnabled && {
        key: 'hpi',
        label: entryScreenContent.reason_for_visit,
        isComplete: submissionStatus?.hpiSubmitted ?? false,
      },
      isMedicalHistoryEnabled && {
        key: 'medicalHistory',
        label: entryScreenContent.medical_history,
        isComplete: submissionStatus?.medicalHistorySubmitted ?? false,
      },
      isMedicalHistoryEnabled && {
        key: 'pharmacy',
        label: entryScreenContent.pharmacy_medications,
        isComplete: submissionStatus?.pharmacyAllergyMedicationsSubmitted ?? false,
      },
      hasForms && {
        key: 'forms',
        label: entryScreenContent.acknowledge_sign,
        subtext: entryScreenContent.acknowledge_sign_desc,
        isComplete: communiqueData?.sendForms?.consentForm !== true,
      },
    ].filter(Boolean) as IntakeStep[];
  }, [appointmentData, communiqueData]);

  const completedCount = statusItems.filter((item) => item.isComplete).length;
  const allItemsComplete = statusItems.length > 0 && completedCount === statusItems.length;
  const headingText = allItemsComplete ? 'Intake Complete' : 'Intake completion status';
  const descriptionText = allItemsComplete
    ? `Your responses have been sent to ${communiqueData?.organizationName ?? 'your provider'}`
    : 'Review the intake items requested for this appointment.';

  return (
    <section
      aria-labelledby="intake-status-heading"
      className="text-left"
    >
      <h1 id="intake-status-heading" className="mb-2 text-xl font-semibold leading-7 text-gray-800">
        {headingText}
      </h1>
      <p className="text-sm leading-6 text-[#77889C]">
        {descriptionText}
      </p>

      {statusItems.length > 0 ? (
        <IntakeStepList steps={statusItems} />
      ) : (
        <p className="mt-5 rounded-lg bg-gray-50 px-4 py-3 text-gray-600">
          No intake items are required for this appointment.
        </p>
      )}

      {allItemsComplete && (
        <p className="mt-6 rounded-lg border border-populate-green/15 bg-populate-green/10 px-4 py-3 text-sm leading-6 text-teal-800">
          No further action needed
        </p>
      )}
    </section>
  );
}

export default IntakeCompletionStatus;
